import moment from 'moment';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import useRequests from '@ubeya/shared/hooks/account/useRequests';
import { FlexColumn, Loader } from '@ubeya/shared-web/components';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useEmployeeTimeAvailabilities from '@ubeya/shared/hooks/account/useEmployeeTimeAvailabilities';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import {
  AvailabilityWindowStatus,
  resolveAvailabilityWindowForMonth
} from '@ubeya/shared/utils/resolveAvailabilityWindow';
import { getDaysOfWeek, getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import useRequestsTypes, {
  RequestAbsenceKinds,
  RequestTypes,
  RequestTypesSlugs
} from '@ubeya/shared/hooks/generic/useRequestsTypes';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { AddTimeAvailabilityModal } from './AddTimeAvailabilityModal';
import { TimeAvailabilityDay } from './TimeAvailabilityDay';

const Content = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  justify-content: flex-start;
  flex: 1;
`;

const LoaderWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const EmployeeTimeAvailability = ({
  accountId,
  employee,
  selectedBranchId,
  refetchOnAvailabilityChange,
  timeRange
}) => {
  const { t, dateFormat, isAvailabilityWindowEnabled } = useContext(ConfigContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const { employeeId, firstName = '', lastName = '' } = employee || {};

  const daysOptions = useMemo(() => getDaysOfWeek(moment(timeRange.startDate).format(API_DATE_FORMAT)), [timeRange]);

  const from = moment(timeRange.startDate).format(API_DATE_FORMAT);
  const to = moment(timeRange.endDate).format(API_DATE_FORMAT);

  const {
    isLoading,
    timeOffs,
    refetch: refetchEmployeeTimeAvailability,
    timeAvailabilities,
    dailyAvailability,
    deleteTimeAvailability,
    addTimeAvailabilities,
    deleteTimeOff
  } = useEmployeeTimeAvailabilities({
    accountId,
    employeeId,
    from,
    to,
    refetchOnAvailabilityChange
  });

  // Informational only (admins can edit regardless of the window), so computed client-side off the
  // branch's saved config - same reasoning/util as the settings panel's live preview, no backend
  // round-trip for something non-authoritative.
  const { mappedBranches } = useBranches();
  const { startDay, endDay } = mappedBranches[selectedBranchId]?.availabilityWindowConfig || {};

  const getAvailabilityWindowStatus = useCallback(
    (date) => {
      if (!isAvailabilityWindowEnabled || !startDay || !endDay) {
        return null;
      }

      const monthStatus = resolveAvailabilityWindowForMonth({ startDay, endDay, targetMonth: date });

      return monthStatus.status !== AvailabilityWindowStatus.OPEN ? monthStatus : null;
    },
    [isAvailabilityWindowEnabled, startDay, endDay]
  );

  const { requestTypes } = useRequestsTypes();
  const { addRequest } = useRequests({ enabled: false });

  const absenceRequestTypes = useMemo(
    () => (requestTypes || []).find(({ type }) => type === RequestTypesSlugs.ABSENCE),
    [requestTypes]
  );

  const absenceRequestTypesKinds = useMemo(
    () => absenceRequestTypes?.kinds?.filter(({ slug }) => slug !== RequestAbsenceKinds.WORK_FROM_HOMR) || [],
    [absenceRequestTypes?.kinds]
  );

  const timeOffRequestOptions = useMemo(
    () => (absenceRequestTypesKinds || [])?.map(({ id, slug }) => ({ value: id, label: t(slug) })),

    [absenceRequestTypesKinds, t]
  );

  const mappedTimeOffRequestOptions = useMemo(
    () =>
      (absenceRequestTypesKinds || []).reduce((all, kind) => {
        all[kind.id] = { ...kind, slug: t(kind.slug || '') };
        return all;
      }, {}),
    [absenceRequestTypesKinds, t]
  );

  const listData = useMemo(
    () =>
      daysOptions.map((day) => ({
        day,
        dailyAvailability: dailyAvailability.find((entry) => entry.date === day?.momentDate.format(API_DATE_FORMAT)),
        periods: [
          ...timeAvailabilities
            .filter(({ startTime, endTime }) =>
              day?.momentDate.isBetween(moment(startTime).startOf('day'), moment(endTime).endOf('day'), undefined, '[]')
            )
            .map((data) => ({ ...data, type: 'availability' })),

          ...timeOffs
            .filter(({ startTime, endTime }) =>
              day?.momentDate.isBetween(moment(startTime).startOf('day'), moment(endTime).endOf('day'), undefined, '[]')
            )
            .map((data) => ({ ...data, type: 'timeoff' }))
        ]
      })),
    [daysOptions, dailyAvailability, timeAvailabilities, timeOffs]
  );

  const onDeletePeriod = useCallback(
    (data) => {
      switch (data.type) {
        case 'availability':
          return deleteTimeAvailability({ id: data.id });

        case 'timeoff':
          return deleteTimeOff({ id: data.id });
        default:
          return null;
      }
    },
    [deleteTimeAvailability, deleteTimeOff]
  );

  const onSave = useCallback(
    async ({ dates, endTime, startTime, status, absenceType }) => {
      if (absenceType) {
        const { startTime: formattedStartTime, endTime: formattedEndTime } = getTimeRangeWithDates({
          date: dates[0],
          startTime,
          endTime,
          includeEqualStartEndTIme: false
        });

        await addRequest({
          typeId: RequestTypes.ABSENCE,
          employeeId,
          branchId: selectedBranchId,
          jobTypeId: absenceType,
          startTime: formattedStartTime,
          endTime: formattedEndTime
        });

        await Promise.all([refetchEmployeeTimeAvailability(), refetchOnAvailabilityChange?.()]);
        return setShowModal(false);
      }
      addTimeAvailabilities({
        timeAvailabilities: dates.map((date) => {
          const { startTime: formattedStartTime, endTime: formattedEndTime } = getTimeRangeWithDates({
            date,
            startTime,
            endTime
          });
          return {
            startTime: formattedStartTime,
            endTime: formattedEndTime,
            status
          };
        })
      });
      setShowModal(false);
    },
    [
      addRequest,
      addTimeAvailabilities,
      employeeId,
      refetchEmployeeTimeAvailability,
      refetchOnAvailabilityChange,
      selectedBranchId
    ]
  );

  const employeeName = useMemo(() => `${firstName} ${lastName}`, [firstName, lastName]);

  const isContentLoading = useMemo(() => isLoading && (timeAvailabilities || []).length === 0, [
    isLoading,
    timeAvailabilities
  ]);

  return (
    <Content>
      {showModal && (
        <AddTimeAvailabilityModal
          t={t}
          dateFormat={dateFormat}
          mappedTimeOffRequestOptions={mappedTimeOffRequestOptions}
          timeOffRequestOptions={timeOffRequestOptions}
          key={selectedDay}
          employeeName={employeeName}
          onClose={() => setShowModal(false)}
          onSave={onSave}
          daysOptions={daysOptions}
          date={selectedDay}
        />
      )}
      {isContentLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        listData.map((item, index) => (
          <TimeAvailabilityDay
            t={t}
            key={index}
            dateFormat={dateFormat}
            mappedTimeOffRequestOptions={mappedTimeOffRequestOptions}
            setSelectedDay={setSelectedDay}
            date={item.day?.date}
            availabilityWindowStatus={getAvailabilityWindowStatus(item.day?.date)}
            periods={item.periods}
            dailyAvailability={item.dailyAvailability}
            onCreateRequestClicked={() => setShowModal(true)}
            onDeletePeriod={onDeletePeriod}
          />
        ))
      )}
    </Content>
  );
};

export default EmployeeTimeAvailability;
